import { useState } from "react";
import { Link } from "wouter";
import { Heart, Shield, Users, ShoppingBag, Briefcase, Star, ChevronDown, ChevronUp, Phone, ExternalLink, LifeBuoy, AlertTriangle, ChevronLeft, ChevronRight, Mail, MessageSquare } from "lucide-react";

type HelpItem = {
  name: string;
  detail?: string;
  phone?: string;
  email?: string;
  website?: string;
  tag?: string;
};

type Section = {
  id: string;
  icon: React.ReactNode;
  title: string;
  color: string;
  bg: string;
  borderColor: string;
  intro?: string;
  items: HelpItem[];
};

const SECTIONS: Section[] = [
  {
    id: "crisis",
    icon: <Heart size={20} />,
    title: "Mental Health & Crisis",
    color: "#dc2626",
    bg: "#fef2f2",
    borderColor: "#fca5a5",
    intro: "If you are in immediate danger, call 999. For all other mental health support, the services below are free and available around the clock.",
    items: [
      { name: "Samaritans", detail: "24/7 emotional support — call any time, any reason", phone: "116123", tag: "Free · 24/7" },
      { name: "Breathing Space (Scotland)", detail: "Free, confidential phone line for people in Scotland", phone: "08008385587", tag: "Free · Scotland" },
      { name: "NHS 24 Mental Health Hub", detail: "Call 111 and select option 2 for urgent mental health support", phone: "111", tag: "NHS · 24/7" },
      { name: "Ayrshire Crisis Team", detail: "Ayrshire & Arran NHS — local mental health crisis line", phone: "08000275139", tag: "Free · 24/7" },
      { name: "SAMH", detail: "Scottish Association for Mental Health — information and support", website: "https://www.samh.org.uk", tag: "Scotland" },
      { name: "PAPYRUS HopelineUK", detail: "Specialist support for under 35s at risk of suicide", phone: "08000684141", tag: "Free · Under 35" },
    ],
  },
  {
    id: "drugs",
    icon: <Shield size={20} />,
    title: "Drug & Alcohol Support",
    color: "#7c3aed",
    bg: "#faf5ff",
    borderColor: "#c4b5fd",
    intro: "Getting help is a sign of strength. All services below are confidential and non-judgmental.",
    items: [
      { name: "CRI Ayrshire", detail: "Community Recovery Initiative — Ayrshire drug & alcohol support", phone: "01292611660", tag: "Ayrshire" },
      { name: "FRANK", detail: "Free, confidential drugs advice — 24/7", phone: "03001236600", tag: "Free · 24/7" },
      { name: "Ayrshire Drug & Alcohol Partnership", detail: "North, South & East Ayrshire coordinated support", website: "https://www.nhsaaa.net/services-a-z/alcohol-and-drugs-service/", tag: "NHS Ayrshire" },
      { name: "Scottish Drugs Forum Helpline", detail: "Information and support across Scotland", phone: "08081966060", tag: "Free · Scotland" },
      { name: "Turning Point Scotland (Ayrshire)", detail: "Residential and community rehabilitation", website: "https://www.turningpointscotland.com", tag: "Ayrshire" },
    ],
  },
  {
    id: "ca",
    icon: <Users size={20} />,
    title: "Cocaine Anonymous",
    color: "#d97706",
    bg: "#fffbeb",
    borderColor: "#fcd34d",
    intro: "CA meetings are free, confidential and open to anyone with a desire to stop using cocaine and all other mind-altering substances.",
    items: [
      { name: "CA Scotland Helpline", detail: "Find meetings near you or speak to a member", phone: "08002123029", tag: "Free · Scotland" },
      { name: "Find Ayrshire Meetings", detail: "Search ca.org for weekly meetings in Kilmarnock, Ayr, Irvine and across Ayrshire", website: "https://www.ca.org/meetings", tag: "Meeting Finder" },
      { name: "Online & Phone Meetings", detail: "Join daily international CA meetings from home — no travel needed", website: "https://www.ca.org/online-meetings", tag: "Worldwide · Daily" },
      { name: "NA Scotland (Narcotics Anonymous)", detail: "12-step support for any substance — many Ayrshire meetings", website: "https://www.na.org/meetingsearch/", tag: "Ayrshire Meetings" },
    ],
  },
  {
    id: "food",
    icon: <ShoppingBag size={20} />,
    title: "Food Banks",
    color: "#059669",
    bg: "#f0fdf4",
    borderColor: "#6ee7b7",
    intro: "Food banks are for anyone in need — no referral needed at many. You can also refer a neighbour.",
    items: [
      { name: "Ayr Food Bank", detail: "Trussell Trust network — Ayr and surrounding area", phone: "01292264848", website: "https://ayr.foodbank.org.uk", tag: "South Ayrshire" },
      { name: "Kilmarnock Food Bank", detail: "Serving Kilmarnock and East Ayrshire", phone: "01563545004", website: "https://kilmarnock.foodbank.org.uk", tag: "East Ayrshire" },
      { name: "North Ayrshire Community Food Bank", detail: "Irvine, Saltcoats, Ardrossan, Largs and surrounding areas", phone: "01294213060", tag: "North Ayrshire" },
      { name: "Trussell Trust Food Bank Finder", detail: "Find your nearest food bank across Ayrshire", website: "https://www.trusselltrust.org/get-help/find-a-foodbank/", tag: "Finder" },
      { name: "Community Fridge Scotland", detail: "Free food available daily from community fridges near you", website: "https://hubbub.org.uk/community-fridge", tag: "Scotland" },
      { name: "ADDR Pet Food Bank", detail: "Pet food bank from Ayrshire Drone Dog Rescue — emergency PET food to help families keep their pets during hard times. All enquiries confidential. Facebook: Ayrshire Drone Dog Rescue", phone: "07424425866", email: "ayrshireddr@gmail.com", website: "https://www.addr.org.uk", tag: "Pet Food Bank · Ayrshire" },
    ],
  },
  {
    id: "welfare",
    icon: <Briefcase size={20} />,
    title: "Scottish Welfare & Benefits",
    color: "#1d4ed8",
    bg: "#eff6ff",
    borderColor: "#93c5fd",
    intro: "The Scottish Welfare Fund provides Crisis Grants and Community Care Grants. Contact your local council if you need urgent financial help.",
    items: [
      { name: "South Ayrshire Council — Welfare", detail: "Benefits advice, Scottish Welfare Fund, housing support", phone: "03001230900", website: "https://www.south-ayrshire.gov.uk/benefits", tag: "South Ayrshire" },
      { name: "North Ayrshire Council — Welfare", detail: "Welfare rights, debt advice, benefit claims", phone: "01294310000", website: "https://www.north-ayrshire.gov.uk/housing-and-benefits", tag: "North Ayrshire" },
      { name: "East Ayrshire Council — Welfare", detail: "Benefits, welfare fund, hardship support", phone: "01563576000", website: "https://www.east-ayrshire.gov.uk/benefits", tag: "East Ayrshire" },
      { name: "DWP / Universal Credit", detail: "Apply for Universal Credit, report changes or get urgent help", phone: "08003285644", tag: "Free · 24/7" },
      { name: "Citizens Advice Scotland", detail: "Free, independent advice on benefits, debt, housing and more", phone: "08000281456", website: "https://www.citizensadvice.org.uk/scotland/", tag: "Free" },
      { name: "Shelter Scotland", detail: "Housing and homelessness — emergency advice and legal support", phone: "08008004444", website: "https://scotland.shelter.org.uk", tag: "Free · 24/7" },
    ],
  },
  {
    id: "charities",
    icon: <Star size={20} />,
    title: "Local & National Charities",
    color: "#0891b2",
    bg: "#f0f9ff",
    borderColor: "#7dd3fc",
    items: [
      { name: "Ayrshire Cancer Support", detail: "Free practical and emotional support for cancer patients and families across Ayrshire", phone: "01563538008", website: "https://ayrshirecancersupport.co.uk", tag: "Ayrshire" },
      { name: "Ayrshire SANDS (Baby Loss)", detail: "Support for families affected by pregnancy and baby loss", website: "https://www.sands.org.uk", tag: "Ayrshire" },
      { name: "Ayrshire Hospice", detail: "Palliative care, bereavement support and day services", phone: "01292269200", website: "https://www.ayrshirehospice.org", tag: "Ayrshire" },
      { name: "RNIB Scotland", detail: "Support for people with sight loss", phone: "03031239999", website: "https://www.rnib.org.uk/scotland", tag: "Scotland" },
      { name: "Age Scotland Helpline", detail: "Free, confidential support for older people and carers", phone: "08008520800", website: "https://www.agescotland.org.uk", tag: "Free · Scotland" },
      { name: "Childline", detail: "Free, private support for children and young people under 19", phone: "08001111", website: "https://www.childline.org.uk", tag: "Free · Under 19" },
      { name: "Refuge (Domestic Abuse)", detail: "24/7 National Domestic Abuse Helpline — safe, confidential", phone: "08082000247", website: "https://www.nationaldahelpline.org.uk", tag: "Free · 24/7" },
      { name: "Ayrshire Drone Dog Rescue (ADDR)", detail: "Voluntary, non-profit organisation helping reunite missing dogs with their owners and promoting animal welfare across Ayrshire. Free service. Facebook: Ayrshire Drone Dog Rescue", phone: "07424425866", email: "ayrshireddr@gmail.com", website: "https://www.addr.org.uk", tag: "Free · Ayrshire" },
      { name: "ADDR Pet Food Bank", detail: "Emergency pet food assistance from Ayrshire Drone Dog Rescue to help families keep their pets during difficult times. All enquiries are treated confidentially. Facebook: Ayrshire Drone Dog Rescue", phone: "07424425866", email: "ayrshireddr@gmail.com", website: "https://www.addr.org.uk", tag: "Pet Food Bank · Ayrshire" },
    ],
  },
];

function HelpCard({ item, color }: { item: HelpItem; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <p className="font-bold text-slate-800 text-sm">{item.name}</p>
          {item.detail && <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{item.detail}</p>}
        </div>
        {item.tag && (
          <span
            className="flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ backgroundColor: color + "18", color }}
          >
            {item.tag}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {item.phone && (
          <a
            href={`tel:${item.phone.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: color }}
          >
            <Phone size={12} />
            Call {item.phone}
          </a>
        )}
        {item.email && (
          <a
            href={`mailto:${item.email}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-opacity hover:opacity-80"
            style={{ borderColor: color, color }}
          >
            <Mail size={12} />
            Email
          </a>
        )}
        {item.website && (
          <a
            href={item.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-opacity hover:opacity-80"
            style={{ borderColor: color, color }}
          >
            <ExternalLink size={12} />
            Website
          </a>
        )}
      </div>
    </div>
  );
}

export default function Help() {
  const [expanded, setExpanded] = useState<string | null>("crisis");

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero header */}
      <div className="bg-primary px-4 pt-6 pb-10">
        <div className="container mx-auto max-w-2xl">
          <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4 transition-colors">
            <ChevronLeft size={18} /> Back
          </button>
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center">
              <LifeBuoy size={26} className="text-yellow-400" />
            </div>
            <div>
              {/* Renamed from "Help & Support". This page is a directory of
                  outside services — it cannot take a message, and while it
                  carried that name the site appeared to offer a support
                  channel it did not have. Contacting our team is /support. */}
              <h1 className="text-2xl font-bold text-white">Local Support &amp; Helplines</h1>
              <p className="text-white/70 text-sm">Free crisis, welfare and community resources across Ayrshire</p>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency banner */}
      <a
        href="tel:999"
        className="flex items-center gap-3 bg-red-600 px-4 py-3.5 text-white hover:bg-red-700 transition-colors"
      >
        <div className="container mx-auto max-w-2xl flex items-center gap-3">
          <AlertTriangle size={18} className="flex-shrink-0" />
          <span className="flex-1 text-sm font-semibold">
            In immediate danger? Call <strong>999</strong> now
          </span>
          <Phone size={16} className="flex-shrink-0" />
        </div>
      </a>

      <div className="container mx-auto max-w-2xl px-4 py-6 space-y-3">

        {/* The other kind of help. Everything below this line is an outside
            organisation; if what someone actually wants is us — an order, a
            refund, or to stop marketing texts — this is the way out. */}
        <Link
          href="/support"
          className="flex items-center gap-3 rounded-2xl border border-[#c7d6e6] bg-[#e8eef5] px-4 py-3.5 hover:bg-[#dce6f0] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1e3a5f] focus-visible:ring-offset-2"
        >
          <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0">
            <MessageSquare size={18} className="text-[#1e3a5f]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[#1e3a5f] text-sm">Need help from the Ayrshire Hub team?</p>
            <p className="text-[#1e3a5f]/70 text-xs mt-0.5 leading-relaxed">
              Orders, refunds, your account, or to stop marketing messages — send us a
              request. No account needed.
            </p>
          </div>
          <ChevronRight size={18} className="text-[#1e3a5f]/50 flex-shrink-0" />
        </Link>

        {SECTIONS.map((section) => {
          const isOpen = expanded === section.id;
          return (
            <div key={section.id} className="rounded-2xl overflow-hidden border" style={{ borderColor: section.borderColor }}>
              <button
                className="w-full flex items-center gap-3 px-4 py-4 text-left transition-colors hover:opacity-90"
                style={{ backgroundColor: section.bg }}
                onClick={() => setExpanded(isOpen ? null : section.id)}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: section.color + "20" }}
                >
                  <span style={{ color: section.color }}>{section.icon}</span>
                </div>
                <span className="flex-1 font-bold text-base" style={{ color: section.color }}>{section.title}</span>
                <span style={{ color: section.color }}>
                  {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </span>
              </button>

              {isOpen && (
                <div
                  className="border-t p-4 space-y-3"
                  style={{ borderColor: section.borderColor, backgroundColor: section.bg + "80" }}
                >
                  {section.intro && (
                    <p className="text-slate-600 text-sm leading-relaxed">{section.intro}</p>
                  )}
                  {section.items.map((item, i) => (
                    <HelpCard key={i} item={item} color={section.color} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="text-center text-xs text-slate-400 py-4 leading-relaxed">
          All information correct at May 2026.<br />
          If you know of a local resource missing from this list, please let us know via the{" "}
          <Link href="/community" className="text-primary underline">community board</Link>.
        </div>
      </div>
    </div>
  );
}
